import React, { useEffect, useState } from "react"
import {
    Backdrop,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import AddIcon from "@mui/icons-material/Add"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { Helmet } from "react-helmet"
import { useParams } from "react-router-dom"
import { useSnackbar } from "notistack"

import { api } from "../../../services/Main/Api"
import { getCondoId } from "../../../services/Main/AuthStorage"

import PhoneFormatter from "../../../components/PhoneFormatter/PhoneFormater"
import RentRecipientModalForm from "./RentRecipientModalForm"
import EditBeneficiaryForm from "./EditBeneficiaryForm"

const RentRecipientDetail = () => {
    const { id } = useParams()
    const { enqueueSnackbar } = useSnackbar()

    const [isLoading, setIsLoading] = useState(true)
    const [data, setData] = useState(null)

    // ---- Conta (PIX / TED)
    const [openAccountModal, setOpenAccountModal] = useState(false)

    // ---- Editar Beneficiário
    const [openEditDialog, setOpenEditDialog] = useState(false)

    // ---- Menu excluir conta
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedAccountId, setSelectedAccountId] = useState(null)
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

    /* ===================== API ===================== */

    const getRecipient = () => {
        setIsLoading(true)
        api
            .get(`rent_recipient/${id}/`)
            .then((res) => setData(res.data))
            .catch(() =>
                enqueueSnackbar("Erro ao carregar beneficiário", { variant: "error" })
            )
            .finally(() => setIsLoading(false))
    }

    const handleSubmitEditBeneficiary = (form) => {
        setIsLoading(true)
        api
            .put(`rent_recipient/${id}/`, {
                account: getCondoId(),
                name: form.name,
                cpf_cnpj: form.cpfCnpj,
                phone: form.phone,
                email: form.email,
                country_prefix: form.countryPrefix,
                country: form.country,
            })
            .then(() => {
                enqueueSnackbar("Beneficiário atualizado!", { variant: "success" })
                setOpenEditDialog(false)
                getRecipient()
            })
            .catch(() =>
                enqueueSnackbar("Erro ao atualizar beneficiário", { variant: "error" })
            )
            .finally(() => setIsLoading(false))
    }

    const handleSubmitAccount = (formData) => {
        setIsLoading(true)
        if (formData.type === "pix") {
            let pixKeyValue = formData.pixKey.toString().trim();

            if (parseInt(formData.pixType) === 3) {
                // Tipo telefone → remove tudo que não é número e adiciona +55
                pixKeyValue = pixKeyValue.replace(/\D/g, '');
                pixKeyValue = `+55${pixKeyValue}`;
            }

            if (parseInt(formData.pixType) === 2) {
                // Tipo e-mail → validar formato
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(pixKeyValue)) {
                    throw new Error('E-mail inválido para chave Pix.');
                }
            }

            if (parseInt(formData.pixType) === 1) {
                // Tipo CPF/CNPJ → remover caracteres não numéricos
                pixKeyValue = pixKeyValue.replace(/\D/g, '');
            }
            api.post("/rent_recipient/account/", {
                rent_recipient: id,
                pix_key_value: pixKeyValue,
                pix_key_type: formData.pixType,
                account_type: formData.type.toUpperCase(),
                account: +getCondoId(),
            })
                .then(() => {
                    enqueueSnackbar("Chave PIX cadastrada!", { variant: "success" });
                    getRecipient();
                })
                .catch(() => enqueueSnackbar("Erro ao cadastrar PIX", { variant: "error" }))
                .finally(() => {
                    setIsLoading(false)
                });
        } else {
            api.post("/rent_recipient/account/", {
                rent_recipient: id,
                bank_branch: formData.agency,
                bank_account: formData.accountNumber,
                bank_account_digit: formData.accountDigit,
                bank: formData.bank,
                account_type: formData.type.toUpperCase(),
                bank_account_type: formData.accountType,
                account: +getCondoId(),
            })
                .then(() => {
                    enqueueSnackbar("Conta cadastrada!", { variant: "success" });
                    getRecipient();
                })
                .catch(() => enqueueSnackbar("Erro ao cadastrar conta", { variant: "error" }))
                .finally(() => {
                    setIsLoading(false)
                });
        }
    };



    const deleteAccountById = () => {
        if (!selectedAccountId) return
        setIsLoading(true)
        api
            .delete(`/rent_recipient/account/${selectedAccountId}/`)
            .then(() => {
                enqueueSnackbar("Conta deletada", { variant: "success" })
                getRecipient()
            })
            .catch(() =>
                enqueueSnackbar("Erro ao excluir conta", { variant: "error" })
            )
            .finally(() => {
                setOpenConfirmDialog(false)
                setAnchorEl(null)
                setIsLoading(false)
            })
    }

    /* ===================== Effects ===================== */

    useEffect(() => {
        getRecipient()
    }, [])

    /* ===================== Helpers ===================== */

    const formatPixKey = (value, type) => {
        if (!value) return "-"
        const digits = value.replace(/\D/g, "")
        if (type === 0 && digits.length === 11)
            return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
        if (type === 1 && digits.length === 14)
            return digits.replace(
                /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                "$1.$2.$3/$4-$5"
            )
        if (type === 3)
            return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")
        return value
    }

    /* ===================== Render ===================== */

    return (
        <>
            <Helmet>
                <title>Beneficiário - BlocoB3</title>
            </Helmet>

            <Container maxWidth={false}>
                <Grid container spacing={2}>
                    {/* ================= Beneficiário ================= */}
                    <Grid item xs={12} lg={4}>
                        <Card>
                            <CardHeader
                                title="Beneficiário"
                                sx={{ color: "#F40863" }}
                                action={
                                    <Button onClick={() => setOpenEditDialog(true)} color="inherit">
                                        Editar
                                    </Button>
                                }
                            />
                            <Divider />
                            <CardContent>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>{data?.name}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>CPF/CNPJ</TableCell>
                                            <TableCell>{data?.cpf_cnpj}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Telefone</TableCell>
                                            <TableCell>
                                                <PhoneFormatter phone={data?.phone} country={data?.country} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Email</TableCell>
                                            <TableCell>{data?.email}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ================= Contas ================= */}
                    <Grid item xs={12} lg={8}>
                        <Card>
                            <CardHeader
                                title="Contas Bancárias"
                                sx={{ color: "#F40863" }}
                                action={
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        size="small"
                                        onClick={() => setOpenAccountModal(true)}
                                    >
                                        Adicionar
                                    </Button>
                                }
                            />
                            <Divider />

                            {/* PIX */}
                            {data?.pix_accounts?.length > 0 && (
                                <CardContent>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Chave</TableCell>
                                                <TableCell>Banco</TableCell>
                                                <TableCell>Utilizada em</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.pix_accounts.map((acc) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>
                                                        {formatPixKey(acc.pix_key_value, acc.pix_key_type?.id)}
                                                    </TableCell>
                                                    <TableCell>{acc.bank}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={`${acc.counter} contrato(s)`}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {acc.counter === 0 && (
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    setAnchorEl(e.currentTarget)
                                                                    setSelectedAccountId(acc.id)
                                                                }}
                                                            >
                                                                <MoreVertIcon />
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            )}

                            {/* TED */}
                            {data?.ted_accounts?.length > 0 && (
                                <CardContent>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Agência</TableCell>
                                                <TableCell>Conta</TableCell>
                                                <TableCell>Banco</TableCell>
                                                <TableCell>Utilizada em</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.ted_accounts.map((acc) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>{acc.bank_branch}</TableCell>
                                                    <TableCell>{acc.bank_account}</TableCell>
                                                    <TableCell>{acc.bank}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={`${acc.counter} contrato(s)`}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {acc.counter === 0 && (
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    setAnchorEl(e.currentTarget)
                                                                    setSelectedAccountId(acc.id)
                                                                }}
                                                            >
                                                                <MoreVertIcon />
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </Container>

            {/* ===== Menu Excluir ===== */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => setOpenConfirmDialog(true)}>
                    <DeleteOutlineIcon sx={{ mr: 1 }} />
                    Excluir
                </MenuItem>
            </Menu>

            {/* ===== Confirmação ===== */}
            <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja excluir esta conta?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)}>Cancelar</Button>
                    <Button onClick={deleteAccountById} color="error">
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== Editar Beneficiário ===== */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
                <DialogContent>
                    <EditBeneficiaryForm
                        initialValues={{
                            name: data?.name,
                            cpfCnpj: data?.cpf_cnpj,
                            phone: data?.phone,
                            email: data?.email,
                        }}
                        onSubmit={handleSubmitEditBeneficiary}
                        onCancel={() => setOpenEditDialog(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* ===== Modal Conta ===== */}
            <RentRecipientModalForm
                open={openAccountModal}
                onSubmit={handleSubmitAccount}
                onClose={() => setOpenAccountModal(false)}
                cpfCnpj={data?.cpf_cnpj}
                email={data?.email}
                phone={data?.phone}
            />

            {/* ===== Loading ===== */}
            <Backdrop sx={{ zIndex: 1500 }} open={isLoading}>
                <CircularProgress />
            </Backdrop>
        </>
    )
}

export default RentRecipientDetail
